import { ldFeatureFlags } from '../../utils/LDFeatureFlagsUtils';
import { PropsWithChildren, useEffect, useState } from 'react';
import { RootState, useAppSelector } from '../../redux/store';
import { useBackNavigation } from '../../hooks/useBackNavigation';
import { useFlags, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';

interface LDGuardGateProps extends PropsWithChildren {
  ldFlagName: string;
  isRoute?: boolean;
}

interface LDGuardProps extends LDGuardGateProps {
  clientId: string;
}

function LDGuardGate({ ldFlagName, children, isRoute }: LDGuardGateProps) {
  const navigateBack = isRoute ? useBackNavigation() : undefined;
  const flagEnabled = ldFeatureFlags.getFlagValue(ldFlagName);

  useEffect(() => {
    if (isRoute && !flagEnabled && navigateBack) {
      navigateBack();
    }
  }, [flagEnabled, isRoute, navigateBack]);

  if (!flagEnabled) {
    return null;
  }

  return <>{children}</>;
}

function LDGuardGateWithCustomClient({ ldFlagName, children, isRoute }: LDGuardGateProps) {
  const ldFlags = useFlags();
  const ldClient = useLDClient()!;
  const authContext = useAppSelector((state: RootState) => state.auth.authContext);
  const [isInitialized, setIsInitialized] = useState(false);

  const navigateBack = isRoute ? useBackNavigation() : undefined;
  const flagEnabled = ldFlags[ldFlagName];

  useEffect(() => {
    if (authContext && authContext.email && ldClient) {
      ldClient.identify({
        key: authContext.userId,
        email: authContext.email,
        companyId: authContext.companyId,
        userId: authContext.userId,
      });
    }
  }, [ldClient, authContext]);

  // Wait for the client to be ready
  useEffect(() => {
    if (ldClient) {
      ldClient.waitForInitialization().then(() => {
        setIsInitialized(true);
      });
    }
  }, [ldClient]);

  useEffect(() => {
    // Only navigate back if the client is initialized and the flag is explicitly false
    if (isRoute && isInitialized && flagEnabled === false) {
      navigateBack && navigateBack();
    }
  }, [flagEnabled, isRoute, navigateBack, isInitialized]);

  if (!flagEnabled || !isInitialized) {
    return null;
  }

  return <>{children}</>;
}

function LDGuard({ ldFlagName, clientId, children, isRoute }: LDGuardProps) {
  const [WrappedComponent, setLDGuardComponent] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    if (clientId) {
      const LDComponent = withLDProvider({
        clientSideID: clientId,
        options: {
          streaming: false,
        },
        reactOptions: {
          useCamelCaseFlagKeys: false,
        },
      })(() => (
        <LDGuardGateWithCustomClient ldFlagName={ldFlagName} isRoute={isRoute}>
          {children}
        </LDGuardGateWithCustomClient>
      ));
      setLDGuardComponent(() => LDComponent);
    } else {
      setLDGuardComponent(() => (
        <LDGuardGate ldFlagName={ldFlagName} isRoute={isRoute}>
          {children}
        </LDGuardGate>
      ));
    }
  }, [clientId, isRoute, ldFlagName, children]);

  if (!WrappedComponent) {
    return null;
  }

  return <WrappedComponent />;
}

export default LDGuard;
